/**
 * SendTestEmailModal — operator-facing UI for the send-campaign-flight-test
 * endpoint. Pre-fills the operator's logged-in email; allows additional
 * comma-separated recipients (admin/manager+ only). Calls the test-send
 * edge function and displays per-recipient outcomes.
 *
 * The endpoint enforces:
 *   - Email-format validation (any domain accepted; whitelist removed 2026-05-12)
 *
 * Per-request, per-user-hour, and per-flight-day caps were removed
 * 2026-05-12 to unblock testers running 15-30 recipient batches. The
 * audit table still records every recipient for post-hoc abuse detection.
 *
 * Phase 1B (2026-05-08).
 */

import {
  CRM_SYSTEM_LINK_NO_CATEGORY_WARNING,
  CRM_SYSTEM_LINK_NOTE,
  flightFilledNote,
} from "../../utils/systemTemplateVariables";
import { useState, useEffect } from "react";
import { supabase } from "../../services/supabase";

interface SendResult {
  email: string;
  ok: boolean;
  messageId?: string | null;
  error?: string;
  // 2026-06-05: template observability (SendGrid 202 ≠ delivered).
  template_id?: string | null;
  template_name?: string | null;
  resolved_subject?: string | null;
  template_data_keys?: string[];
  /** 2026-06-08: present only on dry-run responses. */
  dry_run?: boolean;
  mapping_coverage?: MappingCoverageEntry[];
  unresolved_count?: number;
  /** The resolved From / Reply-To with names (mig 1550 for {{assigned_agent}}; every flight since 2026-11-04). */
  sender?: SenderPreview;
}

/** What {{assigned_agent}} resolved to for this test (send-campaign-flight-test). */
interface SenderPreview {
  from_email: string;
  from_name: string | null;
  /** Full From as sent, e.g. "Jane Doe | U.S. LawShield" <jdoe@uslawshield.com> (address alone when no name). */
  from_header?: string;
  reply_to: string | null;
  reply_to_name?: string | null;
  reply_to_header?: string | null;
  from_source: "agent" | "fallback" | "fixed";
  reply_to_source: "agent" | "fallback" | "fixed" | "none";
  outcome: string;
  outcome_label: string;
  resolved_for_contact: string | null;
  /** false: a fixed sender (no agent outcome to show). Absent on an older edge = {{assigned_agent}}. */
  uses_agent?: boolean;
}

function SenderPreviewLine({ s }: { s: SenderPreview }) {
  // Full name AND address, exactly as the header is sent (the address is never inside the name).
  const fromText =
    s.from_header ??
    (s.from_name ? `"${s.from_name}" <${s.from_email}>` : s.from_email);
  const replyName = s.reply_to_name
    ? `"${s.reply_to_name}" <${s.reply_to}>`
    : s.reply_to;
  const replyText = s.reply_to_header ?? (s.reply_to ? replyName : null);
  return (
    <div
      className="text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded p-2"
      data-testid="sender-preview"
    >
      <div data-testid="sender-preview-from">
        <span className="font-mono">From:</span> {fromText}
        {s.from_source === "fallback" ? " (fallback)" : ""}
      </div>
      <div data-testid="sender-preview-reply-to">
        <span className="font-mono">Reply-To:</span>{" "}
        {replyText ?? "(none, replies go to the From address)"}
        {s.reply_to_source === "fallback" ? " (fallback)" : ""}
      </div>
      {s.uses_agent !== false && (
        <div
          className={
            s.outcome === "resolved" ? "text-green-800" : "text-amber-800"
          }
        >
          {s.outcome_label}
          {s.resolved_for_contact ? ` (contact ${s.resolved_for_contact})` : ""}
        </div>
      )}
    </div>
  );
}

/** 2026-06-08: per-mapping coverage row from the dry-run preflight. */
interface MappingCoverageEntry {
  field: string;
  sourceType: string;
  resolved: boolean;
  reason?: string;
}

interface TestSendResponse {
  success: boolean;
  results?: SendResult[];
  error?: string;
  /** Server-issued id surfaced for support investigation. */
  correlationId?: string;
  /** Present when a Required landing-link URL param was satisfied with a
   *  synthetic/override test value. Operators MUST see this: the value in the
   *  delivered link is a test value, NOT recognized by the destination landing
   *  page. (audit MED 7) */
  synthetic_landing_notice?: string;
}

/**
 * Local error shape when the response is non-2xx. Captures everything
 * the user (or whoever they paste the message to) needs to debug:
 * HTTP status, server message, correlationId, and the flight_id we
 * actually sent (rules out UI-vs-server flight_id mismatches).
 */
interface TestSendErrorDetail {
  status?: number;
  statusText?: string;
  error: string;
  correlationId?: string;
  flightId: string;
}

interface SendTestEmailModalProps {
  isOpen: boolean;
  flightId: string;
  flightName: string;
  onClose: () => void;
}

const SendTestEmailModal: React.FC<SendTestEmailModalProps> = ({
  isOpen,
  flightId,
  flightName,
  onClose,
}) => {
  const [recipients, setRecipients] = useState("");
  const [sending, setSending] = useState(false);
  const [response, setResponse] = useState<TestSendResponse | null>(null);
  const [errorDetail, setErrorDetail] = useState<TestSendErrorDetail | null>(
    null,
  );
  // attribute_overrides: free-form list of key=value rows the operator
  // edits inline. Empty rows are dropped before send. Local-only — sent
  // to the edge function as a Record<string, string>; the server applies
  // strict validation (key shape + value-length + max keys).
  const [overrideRows, setOverrideRows] = useState<
    Array<{ key: string; value: string }>
  >([]);
  const [overridesExpanded, setOverridesExpanded] = useState(false);
  // override_suppression: top-level request flag (NOT an attribute override).
  // When checked, recipients on the CRM email_suppressions list still receive
  // the test. Used by operators sending tests to addresses that landed on
  // the suppression list via SendGrid webhooks or historical backfill.
  const [overrideSuppression, setOverrideSuppression] = useState(false);
  // 2026-06-08 preflight (DSSA render-failure response). Dry-run output
  // captured separately so the operator sees the variable-coverage
  // analysis without yet hitting Send. The dry-run is auto-triggered
  // when the operator opens the modal AND on demand via "Re-check."
  const [preflight, setPreflight] = useState<{
    loading: boolean;
    coverage: MappingCoverageEntry[] | null;
    unresolved: number;
    error: string | null;
  }>({
    loading: false,
    coverage: null,
    unresolved: 0,
    error: null,
  });
  const [acknowledgeRenderRisk, setAcknowledgeRenderRisk] = useState(false);
  // {{assigned_agent}} (mig 1550): the preflight shows what the sender resolves to; the operator can preview it as a
  // CRM contact (the test is NOT sent to that contact). Shown only when the flight uses the variable.
  const [senderPreview, setSenderPreview] = useState<SenderPreview | null>(
    null,
  );
  const [previewContactEmail, setPreviewContactEmail] = useState("");

  const runPreflight = async () => {
    setPreflight({ loading: true, coverage: null, unresolved: 0, error: null });
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      const recipientList = recipients
        .split(/[\s,;\n]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const probeList = recipientList.length > 0 ? [recipientList[0]] : [];
      if (probeList.length === 0) {
        const { data: u } = await supabase.auth.getUser();
        const opEmail = u.user?.email ?? "";
        if (opEmail) {
          probeList.push(opEmail);
        }
      }
      if (probeList.length === 0) {
        setPreflight({
          loading: false,
          coverage: null,
          unresolved: 0,
          error: "No recipient available to probe with",
        });
        return;
      }
      const attributeOverrides: Record<string, string> = {};
      for (const row of overrideRows) {
        const k = row.key.trim();
        if (k) {
          attributeOverrides[k] = row.value;
        }
      }
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-campaign-flight-test`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          flight_id: flightId,
          recipient_emails: probeList,
          dry_run: true,
          ...(Object.keys(attributeOverrides).length > 0
            ? { attribute_overrides: attributeOverrides }
            : {}),
          ...(previewContactEmail.trim()
            ? { sender_preview_contact_email: previewContactEmail.trim() }
            : {}),
        }),
      });
      const data = (await res.json()) as TestSendResponse;
      if (!res.ok || !data.results || data.results.length === 0) {
        setPreflight({
          loading: false,
          coverage: null,
          unresolved: 0,
          error: data.error ?? `HTTP ${res.status}`,
        });
        return;
      }
      const first = data.results[0];
      setSenderPreview(first.sender ?? null);
      setPreflight({
        loading: false,
        coverage: first.mapping_coverage ?? [],
        unresolved: first.unresolved_count ?? 0,
        error: null,
      });
    } catch (e) {
      setPreflight({
        loading: false,
        coverage: null,
        unresolved: 0,
        error: (e as Error).message,
      });
    }
  };

  // Reset modal state on each open + pre-fill operator's email +
  // run preflight against the pre-filled recipient.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    (async () => {
      setResponse(null);
      setErrorDetail(null);
      setOverrideRows([]);
      setOverridesExpanded(false);
      setOverrideSuppression(false);
      setAcknowledgeRenderRisk(false);
      setPreflight({
        loading: false,
        coverage: null,
        unresolved: 0,
        error: null,
      });
      const { data: userData } = await supabase.auth.getUser();
      const opEmail = userData.user?.email ?? "";
      setRecipients(opEmail);
      // Auto-fire preflight using operator's email as the probe
      // contact. Operator can re-run after editing recipients /
      // overrides via the "Re-check" button.
      setTimeout(() => runPreflight(), 50);
    })();
  }, [isOpen]);

  const addOverrideRow = () =>
    setOverrideRows((rows) => [...rows, { key: "", value: "" }]);
  const updateOverrideRow = (
    idx: number,
    patch: Partial<{ key: string; value: string }>,
  ) =>
    setOverrideRows((rows) =>
      rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    );
  const removeOverrideRow = (idx: number) =>
    setOverrideRows((rows) => rows.filter((_, i) => i !== idx));

  const handleSend = async () => {
    setSending(true);
    setResponse(null);
    setErrorDetail(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        throw new Error("Not authenticated");
      }

      const recipientList = recipients
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      // Build the overrides object. Drop empty/whitespace-only keys; let
      // the server reject malformed keys so operators get the exact same
      // error message the API contract documents.
      const attributeOverrides: Record<string, string> = {};
      for (const row of overrideRows) {
        const k = row.key.trim();
        if (k) {
          attributeOverrides[k] = row.value;
        }
      }
      const hasOverrides = Object.keys(attributeOverrides).length > 0;

      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-campaign-flight-test`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          flight_id: flightId,
          recipient_emails: recipientList,
          ...(hasOverrides ? { attribute_overrides: attributeOverrides } : {}),
          ...(overrideSuppression ? { override_suppression: true } : {}),
          ...(senderPreview && previewContactEmail.trim()
            ? { sender_preview_contact_email: previewContactEmail.trim() }
            : {}),
        }),
      });
      // Parse the body even on non-2xx so we can surface the server's
      // error message + correlationId. Some 5xx responses won't be
      // valid JSON; handle that gracefully too.
      let data: TestSendResponse | null = null;
      let parseError: string | null = null;
      try {
        data = (await res.json()) as TestSendResponse;
      } catch (e) {
        parseError = (e as Error).message;
      }

      if (!res.ok) {
        // Log once for the operator console so the diagnostic is
        // copy-pasteable. Skipped on success.
        console.error("[SendTestEmailModal] non-2xx response", {
          status: res.status,
          statusText: res.statusText,
          flight_id: flightId,
          correlationId: data?.correlationId,
          error: data?.error,
          parseError,
        });
        setErrorDetail({
          status: res.status,
          statusText: res.statusText,
          error:
            data?.error ??
            (parseError
              ? `Response was not valid JSON: ${parseError}`
              : `HTTP ${res.status} ${res.statusText}`),
          correlationId: data?.correlationId,
          flightId,
        });
        return;
      }

      // 2xx but body says success=false (or body missing): treat as
      // a soft error so the operator still sees actionable detail.
      if (!data || data.success !== true) {
        setErrorDetail({
          status: res.status,
          statusText: res.statusText,
          error: data?.error ?? "Server returned 2xx but success=false",
          correlationId: data?.correlationId,
          flightId,
        });
        return;
      }

      // 2xx with success=true.
      setResponse(data);
    } catch (err) {
      // Network / pre-flight failures. Don't have an HTTP status here.
      setErrorDetail({
        error: (err as Error).message,
        flightId,
      });
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Send test email</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div>
            <p className="text-sm text-gray-500">Flight</p>
            <p className="text-base font-medium text-gray-900">{flightName}</p>
          </div>

          <div>
            <label
              htmlFor="test-recipients"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Recipients
            </label>
            <textarea
              id="test-recipients"
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              placeholder="you@example.com, teammate@example.com"
              rows={2}
              disabled={sending}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
            />
            <p className="mt-1 text-xs text-gray-500">
              Comma-separated. Any valid email address.
            </p>
          </div>

          <div>
            <button
              type="button"
              onClick={() => setOverridesExpanded((v) => !v)}
              className="text-sm font-medium text-gray-700 hover:text-gray-900 flex items-center gap-1"
              aria-expanded={overridesExpanded}
            >
              <span>{overridesExpanded ? "▾" : "▸"}</span>
              Attribute overrides{" "}
              {overrideRows.filter((r) => r.key.trim()).length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs font-medium bg-gray-200 text-gray-700 rounded">
                  {overrideRows.filter((r) => r.key.trim()).length}
                </span>
              )}
              <span className="text-xs font-normal text-gray-500 ml-1">
                (optional)
              </span>
            </button>
            {overridesExpanded && (
              <div className="mt-2 space-y-2">
                <p className="text-xs text-gray-500">
                  Inject contact attributes into this test send without creating
                  a real contact. Useful for previewing state/lifecycle-specific
                  variants. Each key must match a field your template references
                  (e.g. <code className="font-mono">state</code>,{" "}
                  <code className="font-mono">lifecycle_stage</code>).
                </p>
                {overrideRows.length === 0 && (
                  <p className="text-xs italic text-gray-400">
                    No overrides — the synthetic contact will use default values
                    (first_name=Test, last_name=Recipient).
                  </p>
                )}
                {overrideRows.map((row, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <input
                      type="text"
                      value={row.key}
                      onChange={(e) =>
                        updateOverrideRow(idx, { key: e.target.value })
                      }
                      placeholder="attribute_name"
                      disabled={sending}
                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm font-mono focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                    />
                    <span className="text-gray-400 text-sm">=</span>
                    <input
                      type="text"
                      value={row.value}
                      onChange={(e) =>
                        updateOverrideRow(idx, { value: e.target.value })
                      }
                      placeholder="value"
                      disabled={sending}
                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                    />
                    <button
                      type="button"
                      onClick={() => removeOverrideRow(idx)}
                      disabled={sending}
                      className="text-gray-400 hover:text-red-600 px-1"
                      aria-label="Remove override"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addOverrideRow}
                  disabled={sending || overrideRows.length >= 50}
                  className="text-xs text-primary-600 hover:text-primary-800 disabled:text-gray-400"
                >
                  + Add attribute
                </button>
              </div>
            )}
          </div>

          <div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={overrideSuppression}
                onChange={(e) => setOverrideSuppression(e.target.checked)}
                disabled={sending}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
              />
              <span className="text-sm">
                <span className="font-medium text-gray-900">
                  Send anyway (override suppression list)
                </span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  Bypasses the CRM email suppression list for this test. Use
                  when the recipient's address landed on the suppression list
                  (SendGrid webhook, historical backfill, or manual entry) but
                  you still need to receive the test. Logged to the test-send
                  audit trail.
                </span>
              </span>
            </label>
          </div>

          <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900">
            Test sends use a fake voucher (code{" "}
            <code className="font-mono">TEST-PREVIEW</code>) and inject{" "}
            <code className="font-mono">is_test_send = true</code> into the
            template data. They do not touch flight status, email tracking, or
            contact tags. The fake voucher code is not redeemable.
          </div>

          {response && response.success && (
            <div className="space-y-3">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <h3 className="text-sm font-semibold text-green-800 mb-2">
                  Test send dispatched
                </h3>
                <ul className="text-sm space-y-2">
                  {(response.results ?? []).map((r) => (
                    <li
                      key={r.email}
                      className={r.ok ? "text-green-800" : "text-red-700"}
                    >
                      <div className="font-medium">
                        {r.ok ? "✓" : "✗"} {r.email}
                        {!r.ok && r.error ? ` — ${r.error}` : null}
                      </div>
                      {r.sender && (
                        <div className="ml-4 mt-1">
                          <SenderPreviewLine s={r.sender} />
                        </div>
                      )}
                      {r.ok && (
                        <dl className="ml-4 mt-1 text-xs text-gray-600 space-y-0.5">
                          {r.messageId && (
                            <div className="flex gap-2">
                              <dt className="font-mono">SendGrid msg:</dt>
                              <dd>
                                <code className="font-mono text-gray-800">
                                  {r.messageId}
                                </code>{" "}
                                <button
                                  type="button"
                                  className="text-blue-600 hover:text-blue-800 underline"
                                  onClick={() =>
                                    navigator.clipboard.writeText(r.messageId!)
                                  }
                                >
                                  copy
                                </button>
                              </dd>
                            </div>
                          )}
                          {r.template_id && (
                            <div className="flex gap-2">
                              <dt className="font-mono">template:</dt>
                              <dd>
                                <code className="font-mono">
                                  {r.template_id}
                                </code>
                                {r.template_name ? ` (${r.template_name})` : ""}
                              </dd>
                            </div>
                          )}
                          {r.resolved_subject && (
                            <div className="flex gap-2">
                              <dt className="font-mono">subject:</dt>
                              <dd className="break-all">
                                {r.resolved_subject}
                              </dd>
                            </div>
                          )}
                          {r.template_data_keys &&
                            r.template_data_keys.length > 0 && (
                              <details>
                                <summary className="cursor-pointer font-mono text-gray-700">
                                  data keys ({r.template_data_keys.length})
                                </summary>
                                <div className="mt-1 pl-4 break-words">
                                  <code className="font-mono text-gray-700">
                                    {r.template_data_keys.join(", ")}
                                  </code>
                                </div>
                              </details>
                            )}
                        </dl>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
              {response.synthetic_landing_notice && (
                <div className="bg-orange-100 border-2 border-orange-300 rounded-md p-3 text-xs text-orange-900">
                  <strong>⚠ Synthetic landing value:</strong>{" "}
                  {response.synthetic_landing_notice}
                </div>
              )}
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900">
                <strong>Heads up:</strong> a successful response here means
                SendGrid accepted the API call (HTTP 202). It does NOT guarantee
                the message rendered + delivered. Templates can still fail with{" "}
                <code className="font-mono">Template Render Failure</code> or
                drop for other reasons. Check the SendGrid Email Activity feed
                using the message id above to confirm delivery.
              </div>
            </div>
          )}

          {errorDetail && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
              <h3 className="text-sm font-semibold text-red-800">
                Test send failed
              </h3>
              <p className="text-sm text-red-700">{errorDetail.error}</p>
              <dl className="text-xs text-red-700 space-y-0.5">
                {errorDetail.status !== undefined && (
                  <div className="flex gap-2">
                    <dt className="font-mono text-red-600">status:</dt>
                    <dd>
                      {errorDetail.status}
                      {errorDetail.statusText
                        ? ` ${errorDetail.statusText}`
                        : ""}
                    </dd>
                  </div>
                )}
                <div className="flex gap-2">
                  <dt className="font-mono text-red-600">flight_id:</dt>
                  <dd className="font-mono break-all">
                    {errorDetail.flightId}
                  </dd>
                </div>
                {errorDetail.correlationId && (
                  <div className="flex gap-2">
                    <dt className="font-mono text-red-600">correlationId:</dt>
                    <dd className="font-mono break-all">
                      {errorDetail.correlationId}
                    </dd>
                  </div>
                )}
              </dl>
              <p className="text-xs text-red-600">
                Include the values above when reporting this to engineering.
              </p>
            </div>
          )}
        </div>

        {/* 2026-06-08: preflight (template variable coverage). Catches
            the DSSA-class failure where lookups resolve empty and
            SendGrid drops the message post-acceptance with
            "Template Render Failure." */}
        <div className="px-6 pt-2 pb-4 border-t border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">
              Preflight — template variable coverage
            </div>
            <button
              type="button"
              onClick={runPreflight}
              disabled={preflight.loading || sending}
              className="text-xs text-primary-600 hover:text-primary-700 disabled:opacity-50"
            >
              {preflight.loading ? "Checking…" : "Re-check"}
            </button>
          </div>
          {senderPreview && (
            <div className="mb-2 space-y-2" data-testid="sender-preview-panel">
              <div className="text-xs font-semibold text-gray-700">
                {senderPreview.uses_agent === false
                  ? "Sender (as the real send uses it)"
                  : "Sender ({{assigned_agent}})"}
              </div>
              <SenderPreviewLine s={senderPreview} />
              {senderPreview.uses_agent !== false && (
                <>
                  <label
                    htmlFor="test-sender-preview-contact"
                    className="block text-xs text-gray-600"
                  >
                    Preview the sender as this CRM contact (optional; the test
                    is not sent to them). Blank: the contact whose email is the
                    test recipient. Click Re-check to update.
                  </label>
                  <input
                    id="test-sender-preview-contact"
                    type="email"
                    value={previewContactEmail}
                    onChange={(e) => setPreviewContactEmail(e.target.value)}
                    placeholder="member@example.com"
                    disabled={sending}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                  />
                </>
              )}
            </div>
          )}
          {preflight.error && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
              Preflight failed: {preflight.error}. Proceeding will send the raw
              template; SendGrid may drop the message.
            </div>
          )}
          {preflight.coverage &&
            preflight.coverage.some(
              (c) => c.reason === "system_filled_at_send",
            ) && (
              <div className="text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded p-2 mb-2">
                ℹ️ {CRM_SYSTEM_LINK_NOTE}
              </div>
            )}
          {preflight.coverage &&
            flightFilledNote(
              preflight.coverage.some(
                (c) => c.reason === "subject_from_flight",
              ),
              preflight.coverage.some(
                (c) => c.reason === "preheader_from_flight",
              ),
            ) && (
              <div className="text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded p-2 mb-2">
                ℹ️{" "}
                {flightFilledNote(
                  preflight.coverage.some(
                    (c) => c.reason === "subject_from_flight",
                  ),
                  preflight.coverage.some(
                    (c) => c.reason === "preheader_from_flight",
                  ),
                )}
              </div>
            )}
          {preflight.coverage && preflight.unresolved === 0 && (
            <div className="text-xs text-green-800 bg-green-50 border border-green-200 rounded p-2">
              ✓ All {preflight.coverage.length} template variables resolved for
              the probe contact. SendGrid render should succeed.
            </div>
          )}
          {preflight.coverage && preflight.unresolved > 0 && (
            <div className="text-xs text-amber-900 bg-amber-50 border border-amber-300 rounded p-2 space-y-2">
              <div className="font-semibold">
                ⚠ {preflight.unresolved} of {preflight.coverage.length}{" "}
                template variables will be EMPTY for this recipient. SendGrid is
                likely to drop the message with "Template Render Failure."
              </div>
              <details>
                <summary className="cursor-pointer underline">
                  Show unresolved variables
                </summary>
                <table className="w-full text-xs mt-1 bg-white rounded border border-amber-200">
                  <thead className="bg-amber-100">
                    <tr>
                      <th className="px-2 py-1 text-left">Variable</th>
                      <th className="px-2 py-1 text-left">Source</th>
                      <th className="px-2 py-1 text-left">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preflight.coverage
                      .filter((c) => !c.resolved)
                      .map((c) => (
                        <tr key={c.field} className="border-t border-amber-100">
                          <td className="px-2 py-1 font-mono">{c.field}</td>
                          <td className="px-2 py-1">{c.sourceType}</td>
                          <td className="px-2 py-1">
                            {c.reason === "system_link_needs_email_category"
                              ? CRM_SYSTEM_LINK_NO_CATEGORY_WARNING
                              : (c.reason ?? "—")}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </details>
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acknowledgeRenderRisk}
                  onChange={(e) => setAcknowledgeRenderRisk(e.target.checked)}
                  className="h-3.5 w-3.5"
                />
                <span>
                  I understand SendGrid may drop this message. Send anyway.
                </span>
              </label>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={
              sending ||
              !recipients.trim() ||
              (preflight.unresolved > 0 && !acknowledgeRenderRisk)
            }
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title={
              preflight.unresolved > 0 && !acknowledgeRenderRisk
                ? "Acknowledge the render risk above before sending"
                : undefined
            }
          >
            {sending ? "Sending…" : "Send test"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendTestEmailModal;
